import { IconAngleRight, IconTrashAlt } from '@maia/icons';
import {
  ButtonLink,
  cardStyles,
  PageHeader,
  SectionWithHeader,
  showToast,
  SpecList,
  TextButton,
} from '@maia/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { getCustomerOptions } from './../../../../features/customer/customer/queryOptions';
import { twMerge } from 'tailwind-merge';
import { formatAddress, formatDate, getFullName } from '@maia/utils';
import { useMutation } from '@tanstack/react-query';
import { service } from '@maia/customer';
import { queryClient } from './../../../../main';
import Cookies from 'js-cookie';

export const Route = createFileRoute('/_authenticated/customers/new/$guid')({
  component: RouteComponent,
  loader: ({ context: { queryClient }, params: { guid } }) =>
    queryClient.ensureQueryData(getCustomerOptions(guid)),
});

function RouteComponent() {
  const data = Route.useLoaderData();
  const params = Route.useParams();
  const navigate = useNavigate();

  const { mutateAsync: remove, isPending } = useMutation({
    mutationFn: (params: Parameters<typeof service.remove>[number]) =>
      service.remove(params),
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: ['customers'],
      });

      if (response.response?.token) {
        Cookies.set('auth_tokens', JSON.stringify(response.response.token), {
          secure: true,
          sameSite: 'strict',
          expires: new Date(response.response.token.refreshTokenExpiresAt),
        });
      }

      showToast({
        intent: 'success',
        title: 'Klant verwijderd',
        config: { position: 'top-right' },
      });

      navigate({
        to: '/customers',
      });
    },
    onError: () => {
      showToast({
        intent: 'error',
        title: 'Er ging iets mis',
        description:
          'We konden de klant niet verwijderen. Probeer het later opnieuw',
        config: { position: 'top-right' },
      });
    },
  });

  return (
    <div className="layout-sidebar">
      <PageHeader title="Klant toegevoegd" className="area-page-header" />

      <div className="area-page-content content-grid">
        <SectionWithHeader title="Klantgegevens" className="col-span-full">
          <div className={twMerge(cardStyles({ variant: 'outlined' }), 'p-20')}>
            <SpecList
              items={[
                {
                  title: 'Naam',
                  value: getFullName(data.consumer),
                  isHalf: true,
                },
                {
                  title: 'Geboortedatum',
                  value: formatDate(data.consumer.dateOfBirth),
                  isHalf: true,
                },
                {
                  title: 'Adres',
                  value: formatAddress(data.address),
                  isHalf: true,
                },
                {
                  title: 'E-mail',
                  value: data.consumer.email,
                  isHalf: true,
                },
              ]}
            />
          </div>
        </SectionWithHeader>

        <div className="justify-between flex items-center col-span-full">
          <TextButton
            intent="critical"
            iconLeft={IconTrashAlt}
            className="text-nowrap"
            onPress={() =>
              remove({ email: data.consumer.email, guid: params.guid })
            }
            isDisabled={isPending}
          >
            Verwijder klant
          </TextButton>

          <ButtonLink
            to="/customers/$guid"
            params={{ guid: params.guid }}
            intent="primary"
            iconRight={IconAngleRight}
            isDisabled={isPending}
          >
            Naar klant
          </ButtonLink>
        </div>
      </div>

      <div className="area-page-sidebar invisible" />
    </div>
  );
}
